import type { ApiProduct, ApiStorefrontCatalog, ApiTaxon } from '../api/types'
import { PRODUCT_KIND } from '../constants/api'
import { EXPRESS_SHOP_SLUGS, TAXON_PAGE_ROUTES } from '../constants/shop'

export function storefrontProducts(catalog: ApiStorefrontCatalog): ApiProduct[] {
  const seen = new Set<string>()
  return [
    ...catalog.inRange,
    ...catalog.standard,
    ...(catalog.plants ?? []),
    ...(catalog.nearby ?? []),
  ].filter(
    (product) => {
      if (seen.has(product.id)) return false
      seen.add(product.id)
      return true
    },
  )
}

export function productMatchesTaxonSlug(product: ApiProduct, slug: string): boolean {
  if (product.primaryTaxon?.slug === slug) return true
  return (product.taxons ?? []).some((taxon) => taxon.slug === slug)
}

export function filterProductsByTaxonSlug(
  products: ApiProduct[],
  slug: string,
): ApiProduct[] {
  return products.filter((product) => productMatchesTaxonSlug(product, slug))
}

export function isExpressShopSlug(slug: string): boolean {
  return (EXPRESS_SHOP_SLUGS as readonly string[]).includes(slug)
}

export function taxonPageRoute(slug: string | undefined): string | undefined {
  return slug ? TAXON_PAGE_ROUTES[slug] : undefined
}

function childTaxonSlugs(taxons: ApiTaxon[], parentSlug: string): string[] {
  const parent = taxons.find((taxon) => taxon.slug === parentSlug)
  if (!parent) return []
  return taxons
    .filter((taxon) => taxon.parentId === parent.id)
    .map((taxon) => taxon.slug)
}

export function filterShopProducts<T extends ApiProduct>(
  products: T[],
  slug: string,
  taxons: ApiTaxon[] = [],
): T[] {
  if (isExpressShopSlug(slug)) {
    return products.filter(
      (product) =>
        product.isExpressEligible || productMatchesTaxonSlug(product, slug),
    )
  }

  const matchSlugs = new Set([slug, ...childTaxonSlugs(taxons, slug)])
  return products.filter((product) => {
    if (product.primaryTaxon?.slug && matchSlugs.has(product.primaryTaxon.slug)) {
      return true
    }
    return (product.taxons ?? []).some((taxon) => matchSlugs.has(taxon.slug))
  })
}

export function resolveShopTaxonSlug(pathname: string, paramSlug?: string): string {
  if (pathname === '/shop/occasions' || pathname === '/shop/occasions/') {
    return 'occasion'
  }
  if (pathname.startsWith('/shop/occasions/') && paramSlug) {
    return paramSlug
  }
  return paramSlug?.trim() || ''
}

export function findTaxonBySlug(taxons: ApiTaxon[], slug: string): ApiTaxon | null {
  return taxons.find((taxon) => taxon.slug === slug) ?? null
}

export function pickRelatedProducts(
  products: ApiProduct[],
  current: { id: string; primaryTaxonId: string | null; taxonIds: readonly string[] },
  limit: number,
): ApiProduct[] {
  const taxonIds = new Set(
    [current.primaryTaxonId, ...current.taxonIds].filter(
      (id): id is string => Boolean(id),
    ),
  )
  const others = products.filter(
    (product) =>
      product.id !== current.id && product.kind === PRODUCT_KIND.standard,
  )
  const related = others.filter((product) => productSharesTaxon(product, taxonIds))
  const rest = others.filter((product) => !related.includes(product))
  return [...related, ...rest].slice(0, limit)
}

function productSharesTaxon(product: ApiProduct, taxonIds: Set<string>): boolean {
  if (taxonIds.size === 0) return false
  if (product.primaryTaxonId && taxonIds.has(product.primaryTaxonId)) return true
  return (product.taxons ?? []).some((taxon) => taxonIds.has(taxon.id))
}

export function shopTitleFromTaxon(taxon: ApiTaxon | null, slug: string): string {
  if (taxon?.name) return taxon.name
  if (!slug) return 'Shop'
  return slug
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}
